/******/ (() => { // webpackBootstrap
/* eslint-disable */
if (!customElements.get('product-information')) {
  customElements.define('product-information', class ProductInformation extends HTMLElement {
    constructor() {
      super();
      this.dataCache = new Map(); // Cache for pre-fetched data
      this.init();
    }

    init() {
      this.cacheDOMElements();
      this.bindEventHandlers();
    }

    cacheDOMElements() {
      /* ===== Cache any information that we'll need later ===== */
      this.sectionId = this.getAttribute('data-section-id');
      this.originalSectionId = this.getAttribute('data-original-section-id');
      this.isQuickView = this.dataset.quickView === 'true';
      this.productForm = this.querySelector('product-form');
      this.stickyContainer = this.classList.contains('product-sticky') ? this : null;
      this.stickyHeader = document.querySelector('.header-section:has(.header-is-sticky)');
      this.enableURLUpdate = this.dataset.enableUrlUpdate === 'true';
      this.mainProductURL = this.dataset.url;
      this.showOnlyVariantMedia = this.dataset.showOnlyVariantMedia === 'true';
    }

    connectedCallback() {
      /* ===== Gather options for the current variant, handle sticky form offset, attach event listeners ===== */
      this.setStickyOffset();
      this.attachEventListeners();
      this.prefetchVariantData();
      this.isCombinedListing = this.dataset.isCombinedListing === 'true';
      // Apply cross-option filter only when a variant is actively selected.
      // Without ?variant= the server intentionally renders all design/size values; do not hide any.
      if (this.dataset.hasSelectedVariant === 'true') {
        this.filterDependentOptionsBySelected();
      }
      // For products with >250 variants Liquid truncates product.variants, so the
      // inline matrix is incomplete. Pull the full list from the AJAX endpoint and
      // replace it. Fire-and-forget — fails silently if the request errors.
      this.ensureFullVariantsMatrix();
      this.setupAtcPrice();
    }

    disconnectedCallback() {
      /* ===== Clean up event listeners ===== */
      this.removeEventListeners();
      this.atcPriceObserver?.disconnect();
    }

    setupAtcPrice() {
      /* ===== Mirror the shown price into the ATC label (PDP only, opt-in via payment-buttons show_price_in_button) ===== */
      this.atcPriceObserver?.disconnect();
      if (!this.productForm?.querySelector('[data-atc-price]')) return;
      // Observe this element, not the button: the button is replaceWith()-swapped on every
      // variant change, and the price can be rewritten later by the Embroidery / Snap apps.
      this.atcPriceObserver = new MutationObserver(() => this.syncAtcPrice());
      this.atcPriceObserver.observe(this, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled'],
      });
      this.syncAtcPrice();
    }

    syncAtcPrice() {
      const addButton = this.productForm?.querySelector('[name="add"]');
      const label = addButton?.querySelector('[data-add-to-cart-text]');
      if (!label) return;

      // handleInvalidVariant() resets the label with textContent, which drops the price span.
      let priceEl = label.querySelector('[data-atc-price]');
      if (!priceEl) {
        priceEl = document.createElement('span');
        priceEl.setAttribute('data-atc-price', '');
        priceEl.className = 'atc-price';
        label.appendChild(priceEl);
      }

      const priceSpan = this.querySelector('[data-product-price] span[data-price-text]');
      const shownPrice = priceSpan ? priceSpan.textContent.trim() : '';
      const priceUnavailable = (priceSpan?.getAttribute('data-unavailable-text') || '').trim();
      const labelText = Array.from(label.childNodes)
        .filter(node => node !== priceEl)
        .map(node => node.textContent)
        .join('')
        .trim();

      const showPrice = !addButton.disabled
        && shownPrice !== ''
        && shownPrice !== priceUnavailable
        && labelText !== label.getAttribute('data-unavailable-text');
      const nextText = showPrice ? ` · ${shownPrice}` : '';

      // Write only on change: the write itself is a mutation this observer sees.
      if (priceEl.textContent !== nextText) priceEl.textContent = nextText;
    }

    bindEventHandlers() {
      /* ===== Bind methods to maintain the 'this' context ===== */
      this.resizeHandler = this.handleResize.bind(this);
      this.onQuantityChange = this.handleQuantityChange.bind(this);
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onCartAdded = this.handleCartAdded.bind(this);
      this.resetQuantityInput = this.resetQuantityInput.bind(this);
      this.prefetchVariantData = this.prefetchVariantData.bind(this);
      this.resetQuantityInput = this.resetQuantityInput.bind(this);
    }

    attachEventListeners() {
      // On resize we need to update the sticky form offset
      window.addEventListener('resize', this.resizeHandler);
      // Listen for quantity changes
      eventBus.on('qty:change', this.onQuantityChange);
      // Listen for variant changes
      eventBus.on('variant:change', this.onVariantChange);
      // Listen for cart added events
      eventBus.on('cart:added', this.onCartAdded);
    }

    removeEventListeners() {
      /* ===== Remove any event listeners we've attached ===== */
      window.removeEventListener('resize', this.resizeHandler);
      eventBus.off('qty:change', this.onQuantityChange);
      eventBus.off('variant:change', this.onVariantChange);
      eventBus.off('cart:added', this.onCartAdded);
    }

    prefetchVariantData() {
      const variants = this.querySelectorAll('[data-product-variant]');
      if (!variants.length) return;

      variants.forEach(variant => {
        const productFetchUrl = variant.getAttribute('data-product-fetch-url');

        if (productFetchUrl && !this.dataCache.has(productFetchUrl)) {
          this.fetchProductData(productFetchUrl).catch(error => {
            console.error(`Failed to fetch data for ${productFetchUrl}:`, error);
          });
        }
      });
    }

    async fetchProductData(productFetchUrl) {
      try {
        const response = await fetch(`${productFetchUrl}&section_id=${this.originalSectionId}`);
        if (!response.ok) {
          throw new Error(`Network response was not ok for ${productFetchUrl}`);
        }
        const data = await response.text();
        const productData = this.extractProductData(data);
        this.dataCache.set(productFetchUrl, productData);
      } catch (error) {
        console.error(`Failed to fetch product data from ${productFetchUrl}:`, error);
      }
    }

    async fetchVariantData(productFetchUrl, context) {
      /* ===== Fetch the product data if it's not already cached ===== */
      if (!this.dataCache.has(productFetchUrl) || window.Shopify.designMode) {
        await this.fetchProductData(productFetchUrl);
      }
      this.updateDOMWithData(context);
    }

    extractProductData(data) {
      /* ===== Parse the fetched data and return the new product data ===== */
      const parser = new DOMParser();
      const htmlDocument = parser.parseFromString(data, 'text/html');
      const productInformation = htmlDocument.querySelector('product-information');
      const quickViewContent = htmlDocument.querySelector('template[data-quick-view-product]')?.content;

      const quickViewContentInformation = quickViewContent?.querySelector('product-information');
      const productDataElement = this.isQuickView && quickViewContentInformation ? quickViewContentInformation : productInformation;

      const productMedia = htmlDocument.querySelector('[data-product-media-wrapper]');
      const quickViewMedia = quickViewContent?.querySelector('[data-product-media-wrapper]');
      const productBadges = this.isQuickView && quickViewMedia ? quickViewMedia.querySelectorAll('[data-product-badge]') : productMedia?.querySelectorAll('[data-product-badge]');

      if (this.isCombinedListing || productBadges.length) {
        const productMediaElement = this.isQuickView && quickViewMedia ? quickViewMedia : productMedia;
        const productDiv = document.createElement('div');
        productDiv.setAttribute('data-url', productInformation?.getAttribute('data-url'));
        productDiv.appendChild(productMediaElement);
        productDiv.appendChild(productDataElement);

        return productDiv.outerHTML;
      }

      return productDataElement.outerHTML;
    }

    handleResize() {
      /* ===== Handle resizing of the window ===== */
      if (window.Shopify.designMode) {
        // If we're in the theme editor we need to update the sticky form offset
        // We only do this in the editor because it's not necessary in the live site
        // because mobile devices can incorrectly trigger resize events when scrolling
        this.setStickyOffset();
      }
    }

    handleQuantityChange(event) {
      /* ===== Handle changes to the quantity input ===== */

      // Update the quantity input value
      this.productForm = this.querySelector('product-form');
      this.formQuantityInput = this.productForm?.querySelector('[name="quantity"]');

      if (this.formQuantityInput && event.value) {
        if (event.sectionId && event.sectionId !== this.sectionId) return;
        this.formQuantityInput.value = event.value;
      }
    }

    setStickyOffset() {
      if (this.stickyContainer && this.stickyHeader) {
        // Set the top offset of the sticky form to the height of the sticky header
        this.stickyContainer.style.top = `${this.stickyHeader.offsetHeight + 30}px`;
      }
    }

    async handleVariantChange(context) {
      console.log('Handling variant change with context:', context);
      /* ===== Handle changes to the variant ===== */
      if (context.sectionId && context.sectionId !== this.sectionId) return;

      // Update the current variant based on the selected options
      this.currentVariant = context.variant;
      this.productFetchUrl = context.fetchURL;
      this.productUrl = context.productURL;

      // If we don't have a valid variant, handle it and return
      if (!this.currentVariant) {
        this.handleInvalidVariant(context);
        return;
      }

      if (!this.productFetchUrl || !this.productUrl) return;

      // If the variant is available, update the UI
      this.toggleAddButton(this.currentVariant.available);
      const isCached = this.dataCache.has(this.productFetchUrl) && !window.Shopify.designMode;

      if (isCached) {
        this.updateDOMWithData(context);
      } else {
        // If the data is not cached, fetch the product data and then update the UI
        await this.fetchVariantData(this.productFetchUrl, context);
      }

      // Re-apply the cross-option filter after the options block has been swapped.
      // The server renders all in-stock design/size values when selected_variant is non-nil,
      // so without this call clicking the last option would visually "expand" the swatches.
      this.filterDependentOptionsBySelected();

      // Reset quantity input to 1 on variant change
      this.resetQuantityInput();

      // Refocus on the option group the user actually changed, so focus never
      // jumps to a different group after the options re-render (WCAG 3.2.2).
      // When an auto-select is in flight the nested variant-change (for the
      // auto-selected dependent option) must land focus back on the user's
      // origin group — not on the dependent group — and release the guard
      // here, after the fetch has settled, rather than via a fixed-duration
      // timer. This closes the async-timing gap where the timer fired before
      // the network fetch completed and the guard was already false when the
      // DOM update ran (#514, #564).
      if (!context.fromSlideChange) {
        if (this._autoSelecting) {
          this.restoreFocusToOptionGroup(this._originOptionIndex);
          this._autoSelecting = false;
        } else {
          this.restoreFocusToOptionGroup(context.originOptionIndex);
        }
      }

      console.log('Variant change handled for variant:', this.currentVariant);
    }

    restoreFocusToOptionGroup(originOptionIndex) {
      /* ===== Return keyboard focus to the user's current option group ===== */
      let target = null;

      if (originOptionIndex != null) {
        const swatch = this.querySelector(
          `[data-product-options] product-swatch[data-option-index="${originOptionIndex}"]`
        );
        target = swatch?.querySelector('input[type="radio"]:checked')
          || swatch?.querySelector('input[type="radio"]');
      }

      // Fall back to the first selected radio only if the origin group is unknown.
      if (!target) {
        target = this.querySelector('input[type="radio"][name^="option-"]:checked');
      }

      if (target) target.focus();
    }

    handleInvalidVariant(context = {}) {
      /* ===== Handle an invalid variant ===== */
      if (context.clearAfterFetch) this._clearAfterFetch = true;
      if (context.clearLastAfterFetch) this._clearLastAfterFetch = true;
      if (context.autoSelectNext) this._autoSelectNextOption = true;
      // Remember which group the user changed so focus can be restored there
      // after the (async) re-render + optional auto-select (WCAG 3.2.2).
      // When an auto-select is in flight (_autoSelecting), the origin group is
      // already stored from the user's real change — don't overwrite it with
      // the dependent group's index. Also clear the guard so it can't get
      // stuck when the auto-selected value itself resolves to an invalid combo.
      if (!this._autoSelecting) {
        this._originOptionIndex = context.fromSlideChange ? null : context.originOptionIndex;
      }
      if (this._autoSelecting) this._autoSelecting = false;
      if (context.preferredLastOptionValue != null) this._preferredLastOptionValue = context.preferredLastOptionValue;

      const productFetchUrl = this.buildRequestUrlWithParams();
      this.fetchNullVariantData(productFetchUrl);

      // If the variant is invalid, disable the add to cart button
      this.toggleAddButton(false);

      // Announce that the current full variant selection has been broken so
      // downstream theme extensions can react. The media gallery rebuild is
      // handled separately via `media:reset` after the null-variant HTML has
      // been fetched (see updateNullVariant) — that event carries the rebuilt
      // markup, while this one only signals the state change.
      eventBus.emit('variant:cleared', {
        sectionId: this.sectionId,
        productURL: this.productUrl,
      });

      // If the variant is invalid, set the add to cart button text to "unavailable"
      const addButton = this.productForm?.querySelector('[name="add"]');
      if (!addButton) return;
      const addButtonSpan = addButton.querySelector('[data-add-to-cart-text]');
      if (!addButtonSpan) return;

      addButtonSpan.textContent = addButtonSpan.getAttribute('data-unavailable-text');

      // If the variant is invalid, set the price to "Unavailable"
      const priceElement = this.querySelector('[data-product-price]');
      if (!priceElement) return;
      const priceElementSpan = priceElement.querySelector('span[data-price-text]');
      if (!priceElementSpan) return;

      priceElementSpan.textContent = priceElementSpan.getAttribute('data-unavailable-text');
    }

    handleCartAdded(event) {
      /* ===== Handle when a product is added to cart ===== */
      if (event.sectionId && event.sectionId !== this.sectionId) return;
      
      // Reset quantity input to 1 after adding to cart
      this.resetQuantityInput();
    }

    resetQuantityInput() {
      /* ===== Reset the quantity input to 1 when variant changes or after cart addition ===== */
      const quantityInput = this.productForm?.querySelector('[name="quantity"]');
      if (quantityInput) {
        quantityInput.value = '1';
      }
    }

    buildRequestUrlWithParams(shouldFetchFullPage = false) {
      const params = [];
      const optionValues = this.selectedOptionValues;

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${this.mainProductURL}?${params.join('&')}`;
    }

    get selectedOptionValues() {
      return Array.from(this.querySelectorAll('li[data-product-variant][selected], input[data-product-variant]:checked')).map(
        ({ dataset }) => dataset.optionValueId
      );
    }

    async fetchAndEmitMediaReset() {
      /* ===== Fetch the cold-load section render (no option_values, no
         ?variant=) so the embedded <product-media> contains the true
         no-variant-selected slide set, then hand it to product-media via
         `media:reset`. Cached per-product because the cold-load markup
         doesn't change between Width-clear events on the same PDP. */
      const url = `${this.mainProductURL}?section_id=${this.originalSectionId || this.sectionId}`;
      try {
        let data = this._coldLoadCache;
        if (!data) {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Network response was not ok for ${url}`);
          }
          data = await response.text();
          this._coldLoadCache = data;
        }
        const html = this.parseHTML(data);
        // The cold-load HTML re-introduces past-cap variant-owned images that
        // Liquid mis-tagged as shared (see project-shopify-250-cap-cascade).
        // Strip those slides from the parsed Document BEFORE handing it to
        // product-media — otherwise handleMediaReset will swap them back into
        // the live DOM and the orange Big Game shot reappears after the user
        // clicks Width (no ?variant=, so the section re-render runs).
        this.stripPastCapVariantSlidesFromHtml(html);
        eventBus.emit('media:reset', {
          sectionId: this.sectionId,
          html: html,
        });
      } catch (error) {
        console.error(`Failed to fetch cold-load product data from ${url}:`, error);
      }
    }

    async fetchNullVariantData(productFetchUrl) {
      /* ===== Fetch the product data if it's not already cached ===== */
      try {
        const response = await fetch(`${productFetchUrl}`);
        if (!response.ok) {
          throw new Error(`Network response was not ok for ${productFetchUrl}`);
        }
        const data = await response.text();
        const productData = this.extractProductData(data);
        this.dataCache.set(productFetchUrl, productData);        
        this.updateNullVariant(data);
      } catch (error) {
        console.error(`Failed to fetch product data from ${productFetchUrl}:`, error);
      }
    }

    updateNullVariant(data) {
      const html = this.parseHTML(data);
      const productInformation = html.querySelector('product-information');

      // Replace the product options with the new data
      this.updateOptions(html, { productURL: productInformation?.getAttribute('data-url') });

      // If triggered by a Width (first option) change, clear auto-selected dependent options
      if (this._clearAfterFetch) {
        this._clearAfterFetch = false;
        this.clearNonFirstOptions();
        // Hide design/size swatches that don't combine with the freshly selected first option.
        // Server still renders all of them as available (override for selected_variant == nil),
        // but once the user has actively picked Width we want to filter the remaining options.
        this.filterDependentOptionsBySelected();
        this.querySelectorAll('[data-product-price] span[data-price-text]').forEach(span => {
          span.textContent = span.getAttribute("data-unavailable-text");
        });

        // Rebuild the media gallery to the true "no variant selected" state.
        // We can't reuse the `html` above because it was fetched with
        // option_values=<width>, which Shopify resolves to a real variant (the
        // first match for that Width), making product.selected_variant non-nil
        // and Liquid render that variant's featured_media as the only slide.
        // Refetch without option_values so Liquid hits the `selected_variant ==
        // nil` branch and emits the shared / product.featured_media slide set.
        this.fetchAndEmitMediaReset();

        // This branch replaced the options block but (unlike the valid-variant
        // path) never restored focus, so it landed on the first focusable
        // element near the top — the inline "Sizing Guide" link. Return focus to
        // the radio the user just selected (WCAG 3.2.2).
        this.restoreFocusToOptionGroup(this._originOptionIndex);
      }

      // If triggered by a middle option (e.g. Design) change, preserve the previously selected
      // options (Width), clear only the last swatch (Size), and re-apply the cross-option filter.
      if (this._clearLastAfterFetch) {
        this._clearLastAfterFetch = false;
        this.clearLastOption();
        this.filterDependentOptionsBySelected();
        this.querySelectorAll('[data-product-price] span[data-price-text]').forEach(span => {
          span.textContent = span.getAttribute("data-unavailable-text");
        });
        // Restore focus to the group the user changed (WCAG 3.2.2), but only
        // when no auto-select of the dependent option is still pending. When
        // _autoSelectNextOption is true, the definitive restore will happen in
        // handleVariantChange's _autoSelecting branch once the full-variant
        // fetch settles — a restore here would target a soon-to-be-replaced
        // element and produce an extra VoiceOver announcement (#564).
        if (!this._autoSelectNextOption) this.restoreFocusToOptionGroup(this._originOptionIndex);
      }

      // If triggered by a Design (second option) change, auto-select the previously selected
      // Collar Size when it's still compatible, otherwise fall back to the first visible option.
      if (this._autoSelectNextOption) {
        this._autoSelectNextOption = false;
        const preferred = this._preferredLastOptionValue;
        this._preferredLastOptionValue = null;
        this.selectFirstOptionInLastSwatch(preferred);
      }
    }

    filterDependentOptionsBySelected() {
      // Hide dependent-option values that don't combine with the currently selected
      // earlier options. Skips when the variant matrix is missing (e.g. combined
      // listing) or when no earlier option is actively selected — in which case the
      // server's "show all" behaviour for null selected_variant is preserved.
      //
      // The set of option names treated as "dependent" comes from
      // snippets/product-options.liquid (option_2_aliases / option_3_aliases),
      // exposed via the [data-option-name-aliases] JSON script tag.
      const matrixEl = this.querySelector('[data-product-variants-matrix]');
      if (!matrixEl) return;

      let variants;
      try {
        variants = JSON.parse(matrixEl.textContent);
      } catch (e) {
        return;
      }
      if (!Array.isArray(variants) || !variants.length) return;

      // Shopify Liquid caps product.variants at 250. If we hit the cap and the full
      // matrix hasn't been fetched yet, skip filtering — we'd hide design/size values
      // whose real variants live past the cap. ensureFullVariantsMatrix() re-runs the
      // filter once the complete list arrives.
      if (variants.length >= 250 && matrixEl.dataset.complete !== 'true') return;

      const aliases = this.getOptionNameAliases();
      const swatches = this.querySelectorAll('[data-product-options] product-swatch');
      if (swatches.length < 2) return;

      const selectedValues = Array.from(swatches).map(swatch => {
        const checked = swatch.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });

      for (let i = 1; i < swatches.length; i++) {
        const swatch = swatches[i];
        const optionName = (swatch.querySelector('.swatch-element')?.getAttribute('data-option') || '').toLowerCase();
        if (!this.isFilterableDependentOption(optionName, i, aliases)) continue;

        const optionKey = `option${i + 1}`;
        swatch.querySelectorAll('.swatch-element').forEach(el => {
          const input = el.querySelector('input[type="radio"]');
          const value = input ? input.value : el.getAttribute('data-value');
          const compatible = variants.some(v => {
            if (!v.available) return false;
            if (v[optionKey] !== value) return false;
            for (let j = 0; j < i; j++) {
              if (selectedValues[j] != null && v[`option${j + 1}`] !== selectedValues[j]) {
                return false;
              }
            }
            return true;
          });
          el.style.display = compatible ? '' : 'none';
        });
      }
    }

    getOptionNameAliases() {
      // Read alias lists emitted by snippets/product-options.liquid. Cached
      // because the lists are static for the page and the script tag survives
      // option-block re-renders.
      if (this._cachedOptionNameAliases) return this._cachedOptionNameAliases;
      const el = this.querySelector('[data-option-name-aliases]');
      const fallback = { option1: [], option2: [], option3: [] };
      if (!el) return fallback;
      try {
        const parsed = JSON.parse(el.textContent);
        this._cachedOptionNameAliases = {
          option1: Array.isArray(parsed.option1) ? parsed.option1 : [],
          option2: Array.isArray(parsed.option2) ? parsed.option2 : [],
          option3: Array.isArray(parsed.option3) ? parsed.option3 : [],
        };
      } catch (e) {
        this._cachedOptionNameAliases = fallback;
      }
      return this._cachedOptionNameAliases;
    }

    isFilterableDependentOption(optionName, swatchIndex, aliases) {
      // Anchor option (option 1 / swatchIndex 0) is never filtered.
      if (swatchIndex === 0) return false;
      // Union option_2 + option_3 aliases — dependent-option names are recognized
      // at whichever non-anchor position they sit at. The Liquid side
      // (snippets/swatch.liquid) applies the same union so initial-load
      // "show all values" and runtime narrowing agree on which options are
      // dependent. JSON shape on [data-option-name-aliases] is unchanged.
      const list2 = Array.isArray(aliases.option2) ? aliases.option2 : [];
      const list3 = Array.isArray(aliases.option3) ? aliases.option3 : [];
      const combined = list2.concat(list3);
      return combined.some(alias => {
        if (typeof alias !== 'string') return false;
        const trimmed = alias.trim().toLowerCase();
        return trimmed !== '' && optionName.includes(trimmed);
      });
    }

    async ensureFullVariantsMatrix() {
      // Liquid's product.variants — as well as the storefront .js/.json endpoints —
      // tops out at 250 entries. For products with more variants we pull the full
      // list through the Lupine "embroidery" app proxy, which has no such cap.
      //
      // Endpoint: /apps/embroidery?path=get-all-variants-data&handle={handle}
      // Expected response: a flat array OR an object with a `variants` key, where
      // each entry has at least { id, available, option1, option2, option3 }.
      //
      // If the proxy is unreachable or returns no extra data, we leave the inline
      // matrix in place so the filter keeps skipping (= every option value stays
      // visible) rather than hide values whose real variants live past the cap.
      const matrixEl = this.querySelector('[data-product-variants-matrix]');
      if (!matrixEl) return;
      if (matrixEl.dataset.complete === 'true') return;

      let inlineVariants;
      try {
        inlineVariants = JSON.parse(matrixEl.textContent);
      } catch (e) {
        return;
      }
      if (!Array.isArray(inlineVariants)) return;

      // Inline matrix is already complete — nothing to fetch.
      if (inlineVariants.length < 250) {
        matrixEl.dataset.complete = 'true';
        return;
      }

      const productUrl = this.dataset.url;
      if (!productUrl) return;
      const handleMatch = productUrl.match(/\/products\/([^/?#]+)/);
      const handle = handleMatch ? handleMatch[1] : null;
      if (!handle) return;

      try {
        const proxyUrl = `/apps/embroidery?path=get-all-variants-data&handle=${encodeURIComponent(handle)}`;        
        const response = await fetch(proxyUrl);
        if (!response.ok) return;
        const responseData = await response.json();
        const fetchedVariants = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData?.variants)
            ? responseData.variants
            : null;
        if (!fetchedVariants) return;

        // If the proxy returned the same number of entries or fewer, the matrix
        // would still be incomplete. Leave it alone so the filter keeps skipping
        // and every option value stays visible.
        if (fetchedVariants.length <= inlineVariants.length) {
          return;
        }

        const fullMatrix = fetchedVariants.map(v => ({
          id: v.id,
          available: v.available !== false,
          option1: v.option1,
          option2: v.option2,
          option3: v.option3,
          // `featured_media_id` is supplied by the embroidery proxy when available
          // (added after the initial proxy contract). It carries the variant's
          // image/media ID so we can correct shared-flag false positives caused
          // by Shopify's 250-variant cap (Liquid can't see past-cap variants'
          // featured_media). Optional — falsy when the proxy hasn't shipped the
          // new field yet, in which case the correction step is a no-op.
          featured_media_id: v.featured_media_id ?? v.image_id ?? null,
        }));
        matrixEl.textContent = JSON.stringify(fullMatrix);
        matrixEl.dataset.complete = 'true';
        // Re-apply the filter now that we have the complete matrix. If no variant
        // is actively selected this is a no-op until the user picks one.
        if (this.dataset.hasSelectedVariant === 'true') {
          this.filterDependentOptionsBySelected();
        }
        // Correct shared-flag false positives. Liquid tags a media as
        // `data-media-shared="true"` whenever its position falls below the first
        // variant-attached image; but for products with >250 variants, past-cap
        // variants' featured_media never appears in `product.images.where(
        // 'attached_to_variant?', true)` (the lookup is itself capped). So
        // genuine variant images that happen to live in positions 1..N-1 get
        // tagged as shared and then wiped client-side by `removeSharedMedia()`.
        // Now that we have the full variants list, strip the false flags.
        this.unmarkVariantOwnedSharedMedia(fullMatrix);
      } catch (e) {
        // Network/parse failure — leave the inline (truncated) matrix in place.
      }
    }

    unmarkVariantOwnedSharedMedia(fullMatrix) {
      // Two-part correction for the "past-cap variant featured_media tagged as
      // shared" problem (see CLAUDE.md / project-shopify-250-cap-cascade):
      //
      // 1. Strip `data-media-shared="true"` from those slides so the client-
      //    side `removeSharedMedia()` (component-product-media.js) doesn't
      //    wipe them when the shopper picks the variant.
      // 2. On a no-variant render (`?variant=` is absent → Liquid renders the
      //    shared band, positions 1..first_variant_start-1), DROP those slides
      //    from the gallery entirely. They look like variant-owned photos to
      //    the shopper (e.g. the orange Big Game collar on the master PDP)
      //    and shouldn't appear until the variant is actually selected. The
      //    sibling Liquid snippet already handles the `?variant=` path by
      //    shifting `first_variant_start` itself.
      if (!Array.isArray(fullMatrix)) return;
      const ownedMediaIds = new Set();
      for (const v of fullMatrix) {
        if (v.featured_media_id) ownedMediaIds.add(String(v.featured_media_id));
      }
      if (ownedMediaIds.size === 0) return;

      const productWrapper = this.closest('[data-product-content-wrapper]');
      const root = productWrapper || document;
      const hasSelectedVariant = this.dataset.hasSelectedVariant === 'true';

      // Walk every slide (not just those still tagged shared) — this method
      // can run twice (once after ensureFullVariantsMatrix, again after a
      // section reload) and the second pass must still remove slides whose
      // shared flag we already stripped on the first pass.
      const allSlides = root.querySelectorAll('[data-slider-main] .swiper-slide, [data-media-thumbnails] .swiper-slide');
      const productMediaEls = new Set();
      allSlides.forEach(slide => {
        const mediaId = slide.dataset.mediaId
          || slide.querySelector('[data-image-id]')?.dataset.imageId;
        if (!mediaId || !ownedMediaIds.has(String(mediaId))) return;

        if (slide.dataset.mediaShared === 'true') {
          delete slide.dataset.mediaShared;
        }
        if (!hasSelectedVariant) {
          const productMedia = slide.closest('product-media');
          if (productMedia) productMediaEls.add(productMedia);
          slide.remove();
        }
      });

      // Refresh swiper instances so removed slides don't leave gaps in
      // mediaCount, pagination, or autoHeight.
      productMediaEls.forEach(pm => {
        const remaining = pm.querySelectorAll('[data-slider-main] .swiper-slide').length;
        pm.dataset.mediaCount = remaining;
        pm.mediaCount = remaining;
        if (typeof pm.resetSlider === 'function') pm.resetSlider();
      });
    }

    // Mirror of unmarkVariantOwnedSharedMedia but operates on a parsed
    // Document fetched cold (no ?variant=, no option_values). Used by
    // fetchAndEmitMediaReset so the html handed to product-media is already
    // free of past-cap variant slides — otherwise the swiper rebuild brings
    // them back into the live DOM. Reads the same owned-media-ids set from
    // the live matrix on `this`.
    stripPastCapVariantSlidesFromHtml(htmlDocument) {
      const matrixEl = this.querySelector('[data-product-variants-matrix]');
      if (!matrixEl || matrixEl.dataset.complete !== 'true') return;
      let matrix;
      try { matrix = JSON.parse(matrixEl.textContent); } catch (e) { return; }
      if (!Array.isArray(matrix)) return;
      const ownedMediaIds = new Set();
      for (const v of matrix) {
        if (v.featured_media_id) ownedMediaIds.add(String(v.featured_media_id));
      }
      if (ownedMediaIds.size === 0) return;

      const slides = htmlDocument.querySelectorAll(
        '[data-slider-main] .swiper-slide, [data-media-thumbnails] .swiper-slide'
      );
      slides.forEach(slide => {
        const mediaId = slide.dataset.mediaId
          || slide.querySelector('[data-image-id]')?.dataset.imageId;
        if (mediaId && ownedMediaIds.has(String(mediaId))) {
          slide.remove();
        }
      });

      // Refresh data-media-count on the parsed product-media elements so the
      // listener's mediaCount catches up before resetSlider runs.
      htmlDocument.querySelectorAll('product-media').forEach(pm => {
        const remaining = pm.querySelectorAll('[data-slider-main] .swiper-slide').length;
        pm.setAttribute('data-media-count', remaining);
      });
    }

    selectFirstOptionInLastSwatch(preferredValue = null) {
      const swatches = this.querySelectorAll('[data-product-options] product-swatch');
      const lastSwatch = swatches[swatches.length - 1];
      if (!lastSwatch) return;
      // Pick a value still visible after filterDependentOptionsBySelected(). Prefer the
      // user's previously selected value when it survived filtering; otherwise pick the
      // first visible value so we never auto-select an incompatible one.
      const visibleElements = Array.from(lastSwatch.querySelectorAll('.swatch-element'))
        .filter(el => el.style.display !== 'none');
      let target = null;
      if (preferredValue != null) {
        target = visibleElements.find(el => {
          const inp = el.querySelector('input[type="radio"]');
          return inp && inp.value === preferredValue;
        }) || null;
      }
      if (!target) target = visibleElements[0] || null;
      const targetInput = target
        ? target.querySelector('input[type="radio"]')
        : lastSwatch.querySelector('input[type="radio"]');
      if (!targetInput) return;

      // Auto-selecting the dependent option must NOT move keyboard focus into
      // that group — focus stays where the user is working (WCAG 3.2.2).
      // Set _autoSelecting before the click so the nested variant-change handler
      // knows to refocus the user's origin group and release the guard itself,
      // after the fetch settles (see handleVariantChange). Neither a sync restore
      // here nor an rAF-based guard clear: the rAF fired before an uncached fetch
      // resolved, leaving _autoSelecting=false when the DOM update ran and causing
      // focus to land on the wrong group; the sync restore on a pre-replace element
      // caused an extra VoiceOver announcement before the DOM settled (#514, #564).
      this._autoSelecting = true;
      targetInput.click();
    }

    clearNonFirstOptions() {
      this.querySelectorAll('[data-product-options] product-swatch').forEach((swatch, index) => {
        if (index === 0) return;
        swatch.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = false; });
        swatch.querySelectorAll('.swatch-element.active').forEach(el => { el.classList.remove('active'); });
      });
    }

    clearLastOption() {
      const swatches = this.querySelectorAll('[data-product-options] product-swatch');
      const lastSwatch = swatches[swatches.length - 1];
      if (!lastSwatch) return;
      lastSwatch.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = false; });
      lastSwatch.querySelectorAll('.swatch-element.active').forEach(el => { el.classList.remove('active'); });
    }

    updateDOMWithData(context) {
      /* ===== Update the UI with the cached data ===== */
      if (this.enableURLUpdate) this.updateURL();

      const data = this.dataCache.get(context.fetchURL);
      const html = this.parseHTML(data);
      this.updateDOM(html, context);
    }

    parseHTML(data) {
      /* ===== Parse the fetched data into a DOM object ===== */
      return new DOMParser().parseFromString(data, 'text/html');
    }

    updateDOM(html, context = {}) {
      /* ===== Update the DOM with the fetched data ===== */
      this.replaceElements(html, context);

      if (context.fromSlideChange) return;

      // Emit an event to let other components know the variant was updated
      eventBus.emit('variant:updated', {
        variant: this.currentVariant,
        sectionId: this.sectionId,
        isCombinedListing: context.isCombinedListing,
        // Carried from the swatch when a "Not seen here" design (no variant media)
        // is selected, so product-media falls back to the product default image.
        resetMediaToDefault: context.resetMediaToDefault,
      });
    }

    toggleAddButton(enable) {
      /* ===== Toggle the add to cart button based on the variant availability ===== */
      const addButton = this.productForm?.querySelector('[name="add"]');
      if (addButton) {
        addButton.toggleAttribute('disabled', !enable);
      }
    }

    updateURL() {
      /* ===== Update the URL with the current variant ID ===== */
      if (this.currentVariant) {
        window.history.replaceState({}, '', `${this.productFetchUrl}`);
      }
    }

    updateOptions(html, context = {}) {
      /* ===== Update the product options with the new data ===== */
      if (!html) return;

      const productInformation = html.querySelector(`product-information[data-url="${context.productURL}"]`);
      if (!productInformation) return;
    
      const currentElements = Array.from(this.querySelectorAll('[data-product-options]'));

      // Update existing options
      currentElements.forEach(currentElement => {
        const currentUpdateId = currentElement.getAttribute('data-update-id');
        const newElement = productInformation.querySelector(`[data-update-id="${currentUpdateId}"]`);
        if (newElement) {
          // Replace the element if it has the data-replace-content attribute, otherwise update the innerHTML
          currentElement.replaceWith(newElement);
        }
      });
    }

    replaceElements(html, context = {}) {
      /* ===== Replace elements in the DOM with new elements ===== */
      if (!html) return;
    
      const productInformation = html.querySelector(`product-information[data-url="${context.productURL}"]`);
      if (!productInformation) return;

      const currentElements = Array.from(this.querySelectorAll('[data-update-id]'));
      const newElements = Array.from(productInformation.querySelectorAll('[data-update-id]'));
    
      // Update existing elements and track their order
      currentElements.forEach(currentElement => {
        const currentUpdateId = currentElement.getAttribute('data-update-id');
        const newElement = productInformation.querySelector(`[data-update-id="${currentUpdateId}"]`);
        if (newElement) {
          // Replace the element if it has the data-replace-content attribute, otherwise update the innerHTML
          newElement.hasAttribute('data-replace-content') ? currentElement.replaceWith(newElement) : currentElement.innerHTML = newElement.innerHTML;
        }
      });
    
      // Add new elements in the correct order
      newElements.forEach(newElement => {
        const newUpdateId = newElement.getAttribute('data-update-id');
        const existingElement = this.querySelector(`[data-update-id="${newUpdateId}"]`);
    
        if (!existingElement) {
          // Determine the correct position to insert the new element
          let inserted = false;
          for (let i = 0; i < currentElements.length; i++) {
            const currentElement = currentElements[i];
            const currentUpdateId = currentElement.getAttribute('data-update-id');
            if (currentUpdateId > newUpdateId) {
              currentElement.insertAdjacentElement('beforebegin', newElement.cloneNode(true));
              inserted = true;
              break;
            }
          }
          // If not inserted, append to the end
          if (!inserted) {
            this.appendChild(newElement.cloneNode(true));
          }
        }
      });

      const productMedia = html.querySelector('[data-product-media-wrapper]');
      const productWrapper = this.closest('[data-product-content-wrapper]');
      const currentProductMedia = productWrapper?.querySelector('[data-product-media-wrapper]');

      // Update the badges if they exist
      const currentBadges = currentProductMedia ? currentProductMedia.querySelectorAll('[data-product-badge]') : null;
      const newBadges = productMedia ? productMedia.querySelectorAll('[data-product-badge]') : null;

      if (currentBadges && newBadges && !this.showOnlyVariantMedia) {
        currentBadges.forEach((badge, index) => {
          const newBadge = newBadges[index];
          if (newBadge) {
            badge.replaceWith(newBadge);
          }
        });
      }
    
      if (this.isCombinedListing && context.isCombinedListing || this.showOnlyVariantMedia) {
        const mediaSources = productMedia?.getAttribute('data-media-sources');
        const currentMediaSources = currentProductMedia?.getAttribute('data-media-sources');

        // The legacy guard skipped the swap whenever `data-media-sources`
        // matched — but Liquid computes that attribute the same way for every
        // past-cap variant on this product (all reuse the shared-band photos),
        // so it can match while the actual rendered slides differ (the new
        // variant's featured_media vs. the prior variant's). Cross-check the
        // first slide's media-id in both the live DOM and the server HTML; if
        // they differ, content really changed and we must swap.
        const firstCurrentSlideId = currentProductMedia
          ?.querySelector('[data-slider-main] .swiper-slide[data-media-id]')
          ?.getAttribute('data-media-id');
        const firstNewSlideId = productMedia
          ?.querySelector('[data-slider-main] .swiper-slide[data-media-id]')
          ?.getAttribute('data-media-id');
        const slidesAreSame = firstCurrentSlideId && firstCurrentSlideId === firstNewSlideId;
        if (mediaSources === currentMediaSources && slidesAreSame) return;

        if (productMedia && currentProductMedia) currentProductMedia.replaceWith(productMedia);

        eventBus.emit('product:media:updated', {
          variant: this.currentVariant,
          sectionId: this.sectionId,
          isCombinedListing: context.isCombinedListing
        });
      }
    
      if (window.Shopify && window.Shopify.PaymentButton) {
        window.Shopify.PaymentButton.init();
      }
    }    
  });
}
/******/ })()
;